import React from 'react';

import Colors from 'common/colors/constants';
import Pill, { type PillStyle } from 'common/common/Pill';
import RadioButtonV2 from 'common/ui/RadioButtonV2';
import RadioGroupV2 from 'common/ui/RadioGroupV2';
import { P } from 'common/ui/Text';
import isCore from 'common/util/isCore';
import isFree, { FreePlanID } from 'common/util/isFree';
import isGrowth from 'common/util/isGrowth';
import isPro from 'common/util/isPro';
import isStarter from 'common/util/isStarter';

import type { Company } from 'common/api/endpoints/viewer';

import 'css/components/company/_ExistingCompaniesForm.scss';

type FormCompany = Pick<Company, '_id' | 'name' | 'subdomain' | 'planID' | 'planTrialID'>;

const PlanPillStyle: { [key: string]: PillStyle } = {
  free: {
    color: Colors.gray110,
    background: Colors.gray30,
  },
  starter: {
    color: Colors.blue110,
    background: Colors.blue30,
  },
  growth: {
    color: Colors.green100,
    background: Colors.green30,
  },
  core: {
    color: Colors.blue110,
    background: Colors.blue30,
  },
  pro: {
    color: Colors.green100,
    background: Colors.green30,
  },
  business: {
    color: Colors.purple110,
    background: Colors.purple30,
  },
};

const PlanPill = ({ planID }: { planID: string }) => {
  let pillLabel = 'Business';
  let pillStyle = PlanPillStyle.business;

  if (isFree(planID)) {
    pillLabel = 'Free';
    pillStyle = PlanPillStyle.free;
  }

  if (isStarter(planID)) {
    pillLabel = 'Starter';
    pillStyle = PlanPillStyle.starter;
  }

  if (isGrowth(planID)) {
    pillLabel = 'Growth';
    pillStyle = PlanPillStyle.growth;
  }

  if (isCore(planID)) {
    pillLabel = 'Core';
    pillStyle = PlanPillStyle.core;
  }

  if (isPro(planID)) {
    pillLabel = 'Pro';
    pillStyle = PlanPillStyle.pro;
  }

  return (
    <Pill className="companyPlan" pillStyle={pillStyle}>
      {pillLabel}
    </Pill>
  );
};

const ExistingCompaniesForm = ({
  companies,
  fieldError,
  selectedCompany,
  setSelectedCompany,
  validCompanies,
  options = { showPlan: true },
}: {
  companies: FormCompany[];
  fieldError: {
    invalidOption: boolean;
  };
  selectedCompany?: string;
  setSelectedCompany: (value: string) => void;
  validCompanies: string[];
  options?: { showPlan: boolean };
}) => {
  return (
    <section className="existingCompanyContainer">
      <RadioGroupV2
        className="companyList"
        error={fieldError.invalidOption}
        onChange={setSelectedCompany}
        value={selectedCompany}
        size="medium"
        name="company">
        {companies.map((company: FormCompany) => (
          <RadioButtonV2
            className="companyListItem"
            disabled={!validCompanies.includes(company._id)}
            key={company.subdomain}
            value={company.subdomain}>
            <div className="companyDetails">
              <div className="companyPrimaryDetails">
                <P className="companyName" variant="bodyMd">
                  {company.name}
                </P>
                <P className="companyDomain" variant="bodySm">
                  {company.subdomain}.canny.io
                </P>
              </div>
              {options.showPlan && (
                <PlanPill planID={company.planTrialID || company.planID || FreePlanID} />
              )}
            </div>
          </RadioButtonV2>
        ))}
      </RadioGroupV2>
    </section>
  );
};

export default ExistingCompaniesForm;
